import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Alert
} from '@mui/material';
import { CheckCircle, Home, Receipt } from '@mui/icons-material';
import { API_BASE_URL } from '../utils/api';

const PaymentSuccess: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const paymentId = searchParams.get('paymentId');
  const [payment, setPayment] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (paymentId) {
      fetchPayment();
    } else {
      setLoading(false);
    }
  }, [paymentId]);

  const fetchPayment = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/payment/${paymentId}`);
      if (!response.ok) throw new Error('Failed to fetch payment details');
      const data = await response.json();
      setPayment(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Container>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <CheckCircle sx={{ fontSize: 80, color: 'success.main', mb: 3 }} />
        <Typography variant="h3" gutterBottom fontWeight="bold">
          Payment Successful!
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
          Thank you for your payment. Your transaction has been completed successfully.
        </Typography>

        {error && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {payment && (
          <Card sx={{ mb: 4, textAlign: 'left' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Payment Details
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Typography variant="body1">
                  <strong>Payment ID:</strong> {payment.id}
                </Typography>
                <Typography variant="body1">
                  <strong>Amount:</strong> R{payment.amount.toFixed(2)}
                </Typography>
                <Typography variant="body1">
                  <strong>Type:</strong> {payment.type.replace('_', ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}
                </Typography>
                <Typography variant="body1">
                  <strong>Status:</strong> {payment.status}
                </Typography>
                <Typography variant="body1">
                  <strong>Date:</strong> {new Date(payment.completedAt || payment.createdAt).toLocaleString()}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        )}

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
          <Button
            variant="contained"
            size="large"
            startIcon={<Home />}
            onClick={() => navigate('/')}
          >
            Go to Home
          </Button>
          {payment && (
            <Button
              variant="outlined"
              size="large"
              startIcon={<Receipt />}
              onClick={() => navigate(`/payment/${payment.id}`)}
            >
              View Receipt
            </Button>
          )}
        </Box>
      </Box>
    </Container>
  );
};

export default PaymentSuccess;



